import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DetailPageTemplate from '../components/templates/DetailPageTemplate';
import HeroSection from '../components/organisms/HeroSection';
import SectionHeader from '../components/molecules/SectionHeader';
import FeatureCard from '../components/molecules/FeatureCard';
import ServiceCard from '../components/molecules/ServiceCard';
import Button from '../components/atoms/Button';
import Text from '../components/atoms/Text';
import Badge from '../components/atoms/Badge';

const VipCarDetail = () => {
  const { t } = useTranslation();
  // 翻訳された機能データを使用
  const features = t('serviceDetail.vipCar.features.list', { returnObjects: true });

  const vehicleTypes = t('serviceDetail.vipCar.vehicles.types', { returnObjects: true });

  const popularRoutes = t('serviceDetail.vipCar.routes.popular', { returnObjects: true });

  const serviceOptions = t('serviceDetail.vipCar.options.services', { returnObjects: true });

  return (
    <DetailPageTemplate
      serviceName={t('serviceDetail.vipCar.title')}
      onContactSubmit={(data) => {
        console.log('VIP Car inquiry:', data);
        alert(t('serviceDetail.vipCar.inquirySuccess'));
      }}
    >
      {({ onContactClick }) => (
        <>
          {/* Hero Section */}
          <HeroSection
            badge={t('serviceDetail.vipCar.badge')}
            title={t('serviceDetail.vipCar.title')}
            subtitle={t('serviceDetail.vipCar.subtitle')}
            description={t('serviceDetail.vipCar.description')}
            primaryAction={{
              text: t('serviceDetail.vipCar.primaryAction'),
              onClick: onContactClick
            }}
            secondaryAction={{
              text: t('serviceDetail.vipCar.secondaryAction'),
              onClick: () => document.getElementById('vehicles')?.scrollIntoView({ behavior: 'smooth' })
            }}
          />

          {/* Service Features */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.vipCar.features.badge')}
                title={t('serviceDetail.vipCar.features.title')}
                subtitle={t('serviceDetail.vipCar.features.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((feature, index) => (
                  <FeatureCard
                    key={index}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Vehicle Types */}
          <section id="vehicles" className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.vipCar.vehicles.badge')}
                title={t('serviceDetail.vipCar.vehicles.title')}
                subtitle={t('serviceDetail.vipCar.vehicles.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {vehicleTypes.map((vehicle, index) => (
                  <ServiceCard
                    key={index}
                    title={vehicle.title}
                    subtitle={vehicle.subtitle}
                    description={vehicle.description}
                    price={vehicle.price}
                    features={vehicle.features}
                    image={t('serviceDetail.vipCar.vehicleImage')}
                    onInquire={onContactClick}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Popular Routes */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.vipCar.routes.badge')}
                title={t('serviceDetail.vipCar.routes.title')}
                subtitle={t('serviceDetail.vipCar.routes.subtitle')}
                className="mb-16"
              />

              <div className="space-y-8">
                {popularRoutes.map((route, index) => (
                  <ServiceCard
                    key={index}
                    title={route.title}
                    description={route.description}
                    duration={route.duration}
                    price={route.price}
                    spots={route.spots}
                    image={t('serviceDetail.vipCar.routeImage')}
                    onBooking={onContactClick}
                    onInquire={onContactClick}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Service Options */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.vipCar.options.badge')}
                title={t('serviceDetail.vipCar.options.title')}
                subtitle={t('serviceDetail.vipCar.options.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {serviceOptions.map((option, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">{option.title}</h3>
                    <Text className="mb-4">{option.description}</Text>
                    <Badge variant="secondary" className="mb-6">{option.price}</Badge>
                    <ul className="space-y-2 mb-6">
                      {option.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      size="small"
                      onClick={onContactClick}
                      className="w-full"
                    >
                      {t('serviceDetail.vipCar.options.inquireDetails')}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Safety & Quality */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.vipCar.safety.badge')}
                title={t('serviceDetail.vipCar.safety.title')}
                subtitle={t('serviceDetail.vipCar.safety.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {t('serviceDetail.vipCar.safety.items', { returnObjects: true }).map((item, index) => (
                  <FeatureCard
                    key={index}
                    icon={item.icon}
                    title={item.title}
                    description={item.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Booking Process */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.vipCar.booking.badge')}
                title={t('serviceDetail.vipCar.booking.title')}
                subtitle={t('serviceDetail.vipCar.booking.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {t('serviceDetail.vipCar.booking.steps', { returnObjects: true }).map((process, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-xl">{process.step}</span>
                    </div>
                    <div className="text-3xl mb-4">{process.icon}</div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">{process.title}</h3>
                    <Text size="sm">{process.description}</Text>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-orange-500 text-white">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-4xl font-bold mb-8">
                {t('serviceDetail.vipCar.cta.title')}
              </h2>
              <Text size="xl" color="white" className="opacity-90 max-w-3xl mx-auto mb-12">
                {t('serviceDetail.vipCar.cta.description')}
              </Text>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="large"
                  onClick={onContactClick}
                  className="bg-white text-orange-500 hover:bg-gray-100"
                >
                  {t('serviceDetail.vipCar.cta.primaryAction')}
                </Button>
                <Link
                  to="/"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white hover:text-orange-500 transition-all duration-200 inline-flex items-center justify-center"
                >
                  {t('serviceDetail.vipCar.cta.secondaryAction')}
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </DetailPageTemplate>
  );
};

export default VipCarDetail;
